import { type SoccerJerseyType } from "components-sbk/src/components/TeamLogo/components/Soccer";
import { replaceKeys, reverseKeyMap } from "shared/src/utils/teamMetadataTransformers";
import { getTeamsMap, hasTeamsMap } from "./useCuiConfig";
import type { TeamsMapGrouped, TeamCompressedData } from "shared/src/utils/parseTeamsData";
import bundledTeamsMap from "tokens/build/js/teams-map";

export type TeamInfoOptionsType = { limitTo?: string[] };

export type TeamInfoReturnType = {
    name: string,
    city: string,
    abbreviation: string,
    shortName: string,
    nickName: string,
    league: string,
    metadata: {
        colors?: { primary: { value: string, type: "color" }, secondary: { value: string, type: "color" }, tertiary?: { value: string, type: "color"} },
        attributes?: {
            jerseyType?: SoccerJerseyType;
        },
        images?: {
            primary?: {
                type?: string,
                name?: string
            }
        } 
    }
} | null;

export const teamInfoKeys = ["name", "shortName", "nickName", "abbreviation", "city", "metadata"];

export function isValidTeamInfoObject(team: unknown): boolean {
    if (!team || typeof team !== "object") return false;
    return teamInfoKeys.every(key => key in (team as Record<string, unknown>));
}

/**
 * Normalizes a search term (handles women's teams, etc.)
 */
function normalizeSearchTerm(searchTerm: string): string {
    if (!searchTerm || typeof searchTerm !== "string") return "";
    let term = searchTerm;
    if (term.includes(" (W)")) term = term.replace(" (W)", "");
    if (term.toLowerCase().includes("women")) term = term.replace(/\(?[Ww]omens?\)?$/gi, "").trim();
    return term.toLowerCase();
}

/**
 * Normalizes competitionType for lookup (college* -> college)
 */
function normalizeCompetitionType(competitionType: string): string {
    if (!competitionType || typeof competitionType !== "string") return "";
    return competitionType.startsWith("college") ? "college" : competitionType;
}

/**
 * Safely transforms compressed team data back to full format
 */
function transformResult(compressed: TeamCompressedData): TeamInfoReturnType {
    try {
        return replaceKeys(compressed, reverseKeyMap) as TeamInfoReturnType;
    } catch {
        return null;
    }
}

/**
 * Looks up a team in a specific competitionType
 */
function lookupInCompetitionType(
    teamsMap: TeamsMapGrouped,
    competitionType: string,
    searchTerm: string
): TeamCompressedData | null {
    try {
        const normalized = normalizeCompetitionType(competitionType);
        if (!normalized) return null;
        const competitionData = teamsMap.data?.[normalized];
        if (!competitionData || typeof competitionData !== "object") return null;
        return competitionData[searchTerm] ?? null;
    } catch {
        return null;
    }
}

// Cache for competition type keys - invalidated when teamsMap.data reference changes
let cachedCompetitionKeys: string[] = [];
let cachedDataRef: Record<string, Record<string, TeamCompressedData>> | null = null;

function getCompetitionKeys(data: Record<string, Record<string, TeamCompressedData>>): string[] {
    if (data !== cachedDataRef) {
        cachedCompetitionKeys = Object.keys(data);
        cachedDataRef = data;
    }
    return cachedCompetitionKeys;
}

/**
 * Looks up a team across all competitionTypes, returns first match
 */
function lookupInAllCompetitionTypes(
    teamsMap: TeamsMapGrouped,
    searchTerm: string
): TeamCompressedData | null {
    try {
        if (!teamsMap.data || typeof teamsMap.data !== "object") return null;
        const competitionTypes = getCompetitionKeys(teamsMap.data);
        for (const type of competitionTypes) {
            const competitionData = teamsMap.data[type];
            if (!competitionData || typeof competitionData !== "object") continue;
            const result = competitionData[searchTerm];
            if (result) return result;
        }
    } catch {
        // Silently fail
    }
    return null;
}

/**
 * Gets the teamsMap to use - prefers window.CUI.teamsMap, falls back to bundled
 */
function getActiveTeamsMap(): TeamsMapGrouped | null {
    if (hasTeamsMap()) {
        return getTeamsMap() ?? null;
    }
    return bundledTeamsMap as TeamsMapGrouped;
}

/**
 * Gets team info from the global teamsMap or bundled fallback
 */
export const getTeamInfo = (searchTerm: string, options?: TeamInfoOptionsType): TeamInfoReturnType => {
    // Guard: invalid input
    if (!searchTerm || typeof searchTerm !== "string") return null;
    
    const teamsMap = getActiveTeamsMap();
    if (!teamsMap || !teamsMap.data) return null;

    const normalizedTerm = normalizeSearchTerm(searchTerm);
    if (!normalizedTerm) return null;

    const limitTo = options?.limitTo;
    const hasLimitTo = Array.isArray(limitTo) && limitTo.length > 0;

    if (hasLimitTo) {
        for (const limitType of limitTo) {
            if (typeof limitType !== "string") continue;
            
            const result = lookupInCompetitionType(teamsMap, limitType, normalizedTerm);
            if (result) return transformResult(result);
            
            // Also try with competitionType prefix (e.g., "college-tennessee")
            const normalized = normalizeCompetitionType(limitType);
            if (normalized) {
                const prefixedResult = lookupInCompetitionType(teamsMap, normalized, `${normalized}-${normalizedTerm}`);
                if (prefixedResult) return transformResult(prefixedResult);
            }
        }
        return null;
    }

    // No limitTo - search all competitionTypes
    const result = lookupInAllCompetitionTypes(teamsMap, normalizedTerm);
    return result ? transformResult(result) : null;
};
